<template>
    <v-card v-bind="$attrs" class="linked-account-edit" outlined>
        <header class="linked-account-edit__header">
            <span class="linked-account-edit__icon" aria-hidden="true">
                <v-icon>{{ mdiLinkVariant }}</v-icon>
            </span>
            <div class="linked-account-edit__heading">
                <span class="linked-account-edit__eyebrow">ACCOUNT ACCESS</span>
                <h3>{{ showCode ? 'Verify your account link' : 'Link a previous account' }}</h3>
                <p>
                    {{ showCode
                        ? 'Enter the verification code sent to the previous account email.'
                        : 'We’ll email a one-time code to confirm you own the previous account.' }}
                </p>
            </div>
            <v-btn icon aria-label="Cancel account linking" @click="cancel">
                <v-icon>{{ mdiClose }}</v-icon>
            </v-btn>
        </header>

        <div class="linked-account-edit__fields">
            <v-text-field
                v-model="oldEmail"
                v-bind="$attrs"
                label="Previous account email"
                autocomplete="email"
                outlined
                dense
                clearable
                :disabled="value.verified"
            />
            <v-text-field
                v-if="showCode"
                v-model="code"
                v-bind="$attrs"
                label="Verification code"
                autocomplete="one-time-code"
                outlined
                dense
                clearable
                :disabled="value.verified"
                @keypress.enter="submit"
            />
        </div>

        <footer class="linked-account-edit__actions">
            <v-btn class="linked-account-edit__cancel" :block="$vuetify.breakpoint.smAndDown" @click="cancel">
                Cancel
            </v-btn>
            <v-btn
                class="linked-account-edit__submit"
                color="primary"
                :block="$vuetify.breakpoint.smAndDown"
                :loading="loading"
                :disabled="!canSubmit || value.verifiedAt"
                @click="submit"
            >
                {{ showCode ? 'Verify account' : 'Send verification code' }}
            </v-btn>
        </footer>
    </v-card>
</template>

<script>
import Vue from 'vue'
import { mdiClose, mdiLinkVariant } from '@mdi/js'
import gql from 'graphql-tag'

export default Vue.extend({
    name: 'AccountLinkedAccountEdit',
    props: {
        value: {
            type: Object,
            required: true,
            default() {
                return {}
            }
        }
    },
    data() {
        return {
            mdiClose,
            mdiLinkVariant,
            oldEmail: null,
            code: null,
            loading: false,
            showCode: false
        }
    },
    watch: {
        value: {
            immediate: true,
            deep: true,
            handler(newVal) {
                this.reset(newVal)
            }
        }
    },
    computed: {
        canSubmit() {
            return !!(this.oldEmail && this.oldEmail.trim() && (!this.showCode || (this.code && this.code.trim())))
        }
    },
    methods: {
        reset(val) {
            this.oldEmail = val.email
            this.code = null
            this.showCode = !!val.createdAt
        },
        cancel() {
            this.$emit('cancel')
            this.reset(this.value)
        },
        submit() {
            if (this.loading || !this.canSubmit) return
            if (this.code) {
                this.submitVerification(this.code)
            } else {
                this.submitLinkRequest()
            }
        },
        submitLinkRequest() {
            this.loading = true
            this.$apollo
                .mutate({
                    mutation: gql`
                        mutation requestAccountLinkCode($oldEmail: String!) {
                            requestAccountLinkCode(oldEmail: $oldEmail) {
                                complete
                                error
                                success
                                uid
                            }
                        }
                    `,
                    variables: {
                        oldEmail: this.oldEmail.trim()
                    }
                })
                .then((data) => {
                    const notification = data.data.requestAccountLinkCode.success
                        ? 'Account link request successfully sent. Please check your email for the verification code.'
                        : 'Failed to request account link code. Please try again later or contact support.'
                    this.$store.dispatch(`notification/set`, notification)
                    if (data.data.requestAccountLinkCode.success) {
                        this.showCode = true
                    }
                    this.$emit('save')
                })
                .catch((e) => {
                    console.error(e)
                    this.$store.dispatch(
                        `notification/set`,
                        'An error occurred while requesting account link code. Please try again later or contact support.'
                    )
                })
                .finally(() => {
                    this.loading = false
                })
        },
        submitVerification(code) {
            this.loading = true
            this.$apollo
                .mutate({
                    mutation: gql`
                        mutation verifyAccountLinkCode($code: String!, $oldEmail: String!) {
                            verifyAccountLinkCode(code: $code, oldEmail: $oldEmail) {
                                complete
                                error
                                success
                                uid
                            }
                        }
                    `,
                    variables: {
                        oldEmail: this.oldEmail.trim(),
                        code: code.trim()
                    }
                })
                .then((data) => {
                    const notification = data.data.verifyAccountLinkCode.success
                        ? 'Account link verification successfully completed.'
                        : 'Failed to verify account link code. Please try again later or contact support.'
                    this.$store.dispatch(`notification/set`, notification)
                    this.$emit('completed')
                })
                .catch((e) => {
                    console.error(e)
                    this.$store.dispatch(
                        `notification/set`,
                        'An error occurred while verifying account link code. Please try again later or contact support.'
                    )
                })
                .finally(() => {
                    this.loading = false
                })
        }
    }
})
</script>

<style scoped>
.linked-account-edit {
    overflow: hidden;
    border-radius: 9px;
}
.linked-account-edit.theme--dark {
    color: #f1f2f5;
    background: rgba(23, 25, 31, 0.38);
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    box-shadow: none !important;
}
.linked-account-edit__header {
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 16px 18px 14px;
}
.linked-account-edit.theme--dark .linked-account-edit__header {
    background: linear-gradient(110deg, rgba(219, 81, 32, 0.1), transparent 75%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.linked-account-edit__icon {
    display: grid;
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    color: #fff;
    background: #db5120;
    border-radius: 10px;
    place-items: center;
}
.linked-account-edit__icon .v-icon {
    color: inherit;
    font-size: 19px;
}
.linked-account-edit__heading {
    min-width: 0;
    flex: 1;
}
.linked-account-edit__eyebrow {
    color: #f2a58b;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 1.2px;
}
.linked-account-edit__heading h3 {
    margin: 3px 0 2px;
    color: inherit;
    font-size: 15px;
    font-weight: 600;
}
.linked-account-edit__heading p {
    margin: 0;
    color: #aeb5c3;
    font-size: 10px;
    line-height: 1.5;
}
.linked-account-edit__header > .v-btn {
    color: #aeb5c3;
}
.linked-account-edit__fields {
    padding: 15px 18px 2px;
}
.linked-account-edit__fields ::v-deep .v-input {
    margin-top: 3px;
}
.linked-account-edit__fields ::v-deep .v-label {
    color: #aeb5c3;
}
.linked-account-edit__fields ::v-deep input {
    color: #f1f2f5;
}
.linked-account-edit__fields ::v-deep .v-input__slot fieldset {
    border-color: rgba(255, 255, 255, 0.18);
}
.linked-account-edit__fields ::v-deep .v-input--is-focused .v-label {
    color: #ff9a74;
}
.linked-account-edit__fields ::v-deep .v-input--is-focused .v-input__slot fieldset {
    border-color: #db5120;
}
.linked-account-edit__actions {
    display: flex;
    justify-content: flex-end;
    gap: 9px;
    padding: 2px 18px 16px;
}
.linked-account-edit__actions .v-btn {
    min-height: 36px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.15px;
    text-transform: none;
}
.linked-account-edit__cancel.v-btn {
    color: #c7cbd4;
    border: 1px solid rgba(255, 255, 255, 0.16);
}
.linked-account-edit__submit.v-btn {
    color: #fff;
    background: #db5120;
}
.linked-account-edit__submit.v-btn:hover {
    background: #e76638;
}
.theme--light.linked-account-edit .linked-account-edit__heading p {
    color: #747985;
}
@media (max-width: 600px) {
    .linked-account-edit__actions {
        flex-direction: column-reverse;
    }
}
</style>
